/**
 * STEP 4 C10 — Admin Users management page (super_admin only).
 *
 * - Lists internal users (admin + user legacy roles)
 * - Create / edit modal (username, password, magasin_role, assigned_magasins)
 * - Soft-disable / enable buttons (R1 strict — no hard delete)
 * - admin_magasin must have ≥1 assigned magasin (UI guard)
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Loader2, Pencil, Percent, Power, PowerOff, ShieldCheck, UserPlus, X, CheckCircle2,
} from 'lucide-react';

import Layout from '../components/Layout';
import MagasinBadge from '../components/MagasinBadge';
import AdminEquipeMagasinsTabs from '../components/AdminEquipeMagasinsTabs';
import CommissionsModal from '../components/CommissionsModal';
import ModalPortal from '../components/ModalPortal';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePermissions } from '../contexts/PermissionsContext';

const MIN_PASSWORD_LEN = 8;

function formatApiDetail(detail) {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item?.msg) {
          const field = Array.isArray(item.loc) ? item.loc.filter((x) => x !== 'body').join('.') : '';
          return field ? `${field}: ${item.msg}` : item.msg;
        }
        return JSON.stringify(item);
      })
      .join(' · ');
  }
  if (typeof detail === 'object') return detail.msg || detail.message || JSON.stringify(detail);
  return String(detail);
}

const ROLES = [
  { code: 'super_admin', labelKey: 'adminUsers.role.super_admin.label', descKey: 'adminUsers.role.super_admin.desc' },
  { code: 'admin_magasin', labelKey: 'adminUsers.role.admin_magasin.label', descKey: 'adminUsers.role.admin_magasin.desc' },
  { code: 'technicien', labelKey: 'adminUsers.role.technicien.label', descKey: 'adminUsers.role.technicien.desc' },
  { code: 'vendeur', labelKey: 'adminUsers.role.vendeur.label', descKey: 'adminUsers.role.vendeur.desc' },
];

const ROLE_BADGE = {
  super_admin: 'bg-red-500/15 text-red-300 border-red-500/30',
  admin_magasin: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  technicien: 'bg-sky-500/15 text-sky-300 border-sky-500/30',
  vendeur: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
};

const emptyForm = {
  username: '', password: '', email: '', telephone: '',
  magasin_role: 'vendeur', assigned_magasins: [],
  pos_only: false,
};

const UserModal = ({ open, initial, onClose, onSubmit, busy, allMagasins, formError }) => {
  const { t } = useLanguage();
  const [form, setForm] = useState(emptyForm);
  const isEdit = Boolean(initial?.id);

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        username: initial.username || '',
        password: '',
        email: initial.email || '',
        telephone: initial.telephone || '',
        magasin_role: initial.magasin_role || 'vendeur',
        assigned_magasins: initial.assigned_magasins || [],
        pos_only: Boolean(initial.pos_only),
      } : emptyForm);
    }
  }, [open, initial]);

  if (!open) return null;

  const magasins = Array.isArray(allMagasins) ? allMagasins : [];

  const toggleMag = (code) => {
    setForm((f) => ({
      ...f,
      assigned_magasins: f.assigned_magasins.includes(code)
        ? f.assigned_magasins.filter((c) => c !== code)
        : [...f.assigned_magasins, code],
    }));
  };

  const submitDisabled = !form.username
    || (!isEdit && !form.password)
    || (form.magasin_role === 'admin_magasin' && form.assigned_magasins.length === 0);

  return (
    <ModalPortal isOpen={open}>
      <div className="w-full max-w-xl bg-gray-900 border border-gray-700 rounded-xl shadow-2xl max-h-[90vh] flex flex-col">
        <header className="flex items-center justify-between px-5 py-3 border-b border-gray-700 shrink-0">
          <h2 className="font-bold text-white">
            {isEdit ? t('adminUsers.modal.editTitle', { username: initial.username }) : t('adminUsers.createUser')}
          </h2>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-white" data-testid="user-modal-close">
            <X className="w-5 h-5" />
          </button>
        </header>
        <div className="p-5 space-y-4 overflow-y-auto flex-1 min-h-0">
          {formError && (
            <div className="p-3 rounded border text-sm bg-red-500/10 border-red-500/30 text-red-300" data-testid="user-form-error">
              {formError}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-gray-400">{t('adminUsers.field.username')} *</span>
              <input
                type="text"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                disabled={isEdit}
                data-testid="user-form-username"
                className="mt-1 w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm disabled:opacity-50"
              />
            </label>
            <label className="block">
              <span className="text-xs text-gray-400">{isEdit ? t('adminUsers.field.passwordEdit') : t('adminUsers.field.passwordCreate', { min: MIN_PASSWORD_LEN })}</span>
              <input
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                data-testid="user-form-password"
                minLength={isEdit ? undefined : MIN_PASSWORD_LEN}
                className="mt-1 w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm"
              />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="text-xs text-gray-400">{t('adminUsers.field.email')}</span>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" />
            </label>
            <label className="block">
              <span className="text-xs text-gray-400">{t('adminUsers.field.telephone')}</span>
              <input type="tel" value={form.telephone} onChange={(e) => setForm({ ...form, telephone: e.target.value })}
                className="mt-1 w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white text-sm" />
            </label>
          </div>
          <div>
            <span className="text-xs text-gray-400">{t('adminUsers.field.role')}</span>
            <div className="mt-1 grid grid-cols-2 gap-2">
              {ROLES.map((r) => (
                <button
                  type="button"
                  key={r.code}
                  onClick={() => setForm({
                    ...form,
                    magasin_role: r.code,
                    pos_only: r.code === 'super_admin' ? false : form.pos_only,
                  })}
                  data-testid={`user-form-role-${r.code}`}
                  className={`text-left p-2 rounded border text-xs transition ${form.magasin_role === r.code ? 'border-emerald-500 bg-emerald-500/10' : 'border-gray-700 bg-gray-800/40 hover:border-gray-500'}`}
                >
                  <div className="font-bold text-white">{t(r.labelKey)}</div>
                  <div className="text-gray-400 mt-0.5">{t(r.descKey)}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <span className="text-xs text-gray-400">
              {t('adminUsers.field.assignedMagasins')} {form.magasin_role === 'admin_magasin' && <span className="text-red-400 font-bold">{t('adminUsers.modal.minOneRequired')}</span>}
              {(form.magasin_role === 'vendeur' || form.magasin_role === 'technicien') && (
                <span className="opacity-60"> {t('adminUsers.modal.ignoredForRole')}</span>
              )}
            </span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {magasins.length === 0 ? (
                <span className="text-xs text-gray-500 italic">{t('adminUsers.modal.loadingMagasins')}</span>
              ) : magasins.map((m) => (
                <button
                  type="button"
                  key={m.code}
                  onClick={() => toggleMag(m.code)}
                  data-testid={`user-form-mag-${m.code}`}
                  className={`px-3 py-1.5 rounded border text-xs font-bold transition ${form.assigned_magasins.includes(m.code) ? 'border-current' : 'border-gray-700 opacity-50 hover:opacity-100'}`}
                  style={{
                    color: m.color || '#10b981',
                    backgroundColor: form.assigned_magasins.includes(m.code) ? `${m.color || '#10b981'}1A` : 'transparent',
                  }}
                >
                  {form.assigned_magasins.includes(m.code) ? '✓ ' : ''}{m.code} <span className="opacity-70 normal-case font-normal">— {m.name}</span>
                </button>
              ))}
            </div>
          </div>
          {form.magasin_role !== 'super_admin' && (
            <label
              className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                form.pos_only
                  ? 'border-amber-500/50 bg-amber-500/10'
                  : 'border-gray-700 bg-gray-800/40 hover:border-gray-500'
              }`}
              data-testid="user-form-pos-only"
            >
              <input
                type="checkbox"
                checked={Boolean(form.pos_only)}
                onChange={(e) => setForm({ ...form, pos_only: e.target.checked })}
                className="mt-1"
              />
              <span>
                <span className="block text-sm font-bold text-white">{t('adminUsers.modal.posOnlyTitle')}</span>
                <span className="block text-xs text-gray-400 mt-0.5">
                  {t('adminUsers.modal.posOnlyDesc')}
                </span>
              </span>
            </label>
          )}
        </div>
        <footer className="flex justify-end gap-2 px-5 py-3 border-t border-gray-700 shrink-0">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm text-gray-300 hover:text-white">
            {t('adminUsers.action.cancel')}
          </button>
          <button
            type="button"
            disabled={submitDisabled || busy}
            onClick={() => onSubmit(form)}
            data-testid="user-form-submit"
            className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm font-bold"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {isEdit ? t('adminUsers.action.save') : t('adminUsers.action.create')}
          </button>
        </footer>
      </div>
    </ModalPortal>
  );
};

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { t, tError } = useLanguage();
  const { isSuper, allMagasins, needsMagasinPick } = usePermissions();

  const [users, setUsers] = useState([]);
  const [limits, setLimits] = useState({ current: 0, max: 3, plan: 'start', can_add: true });
  const [loading, setLoading] = useState(true);
  const [showDisabled, setShowDisabled] = useState(true);
  const [modal, setModal] = useState({ open: false, initial: null });
  const [modalFormError, setModalFormError] = useState(null);
  const [commissionsUser, setCommissionsUser] = useState(null);
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fetchLimits = useCallback(async () => {
    try {
      const r = await api.get('/users/limits');
      setLimits(r.data);
    } catch {
      /* non bloquant — badge masqué si indisponible */
    }
  }, [api]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/users?include_disabled=${showDisabled}`);
      setUsers(r.data?.users || []);
    } catch (e) {
      setFeedback({ type: 'err', msg: tError(formatApiDetail(e?.response?.data?.detail)) || t('adminUsers.error.load') });
    } finally {
      setLoading(false);
    }
  }, [api, showDisabled, tError]);

  useEffect(() => {
    if (isSuper) {
      load();
      fetchLimits();
    }
  }, [load, fetchLimits, isSuper]);

  if (!isSuper) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-6 text-center">
          <ShieldCheck className="w-12 h-12 mx-auto text-red-400 mb-3" />
          <h1 className="text-xl font-bold text-white">{t('adminUsers.denied.title')}</h1>
          <p className="text-gray-400 mt-2">{t('adminUsers.denied.desc')}</p>
          <button onClick={() => navigate('/dashboard')} className="mt-4 text-emerald-400 hover:text-emerald-300">
            ← {t('adminUsers.denied.back')}
          </button>
        </div>
      </Layout>
    );
  }

  const handleSubmit = async (form) => {
    setModalFormError(null);
    if (!modal.initial?.id && (!form.password || form.password.length < MIN_PASSWORD_LEN)) {
      setModalFormError(t('adminUsers.error.passwordMin', { min: MIN_PASSWORD_LEN }));
      return;
    }
    if (modal.initial?.id && form.password && form.password.length < MIN_PASSWORD_LEN) {
      setModalFormError(t('adminUsers.error.passwordMin', { min: MIN_PASSWORD_LEN }));
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      // Don't send empty password on edit.
      if (modal.initial?.id && !payload.password) delete payload.password;
      if (modal.initial?.id) {
        await api.patch(`/admin/users/${modal.initial.id}`, payload);
        setFeedback({ type: 'ok', msg: t('adminUsers.feedback.updated', { username: form.username }) });
      } else {
        await api.post('/admin/users', payload);
        setFeedback({ type: 'ok', msg: t('adminUsers.feedback.created', { username: form.username }) });
      }
      setModal({ open: false, initial: null });
      setModalFormError(null);
      await load();
      await fetchLimits();
    } catch (e) {
      const msg = tError(formatApiDetail(e?.response?.data?.detail)) || t('adminUsers.error.operation');
      setModalFormError(msg);
      setFeedback({ type: 'err', msg });
    } finally {
      setBusy(false);
    }
  };

  const planLabel = limits.plan
    ? limits.plan.charAt(0).toUpperCase() + limits.plan.slice(1)
    : '—';

  const openCreateModal = () => {
    if (!limits.can_add) return;
    if (needsMagasinPick) {
      setFeedback({
        type: 'err',
        msg: t('adminUsers.error.needsMagasinPick'),
      });
      return;
    }
    setModalFormError(null);
    setModal({ open: true, initial: null });
  };

  const openEditModal = (u) => {
    setModalFormError(null);
    setModal({ open: true, initial: u });
  };

  const handleToggle = async (u) => {
    const action = u.is_active ? 'disable' : 'enable';
    try {
      await api.patch(`/admin/users/${u.id}/${action}`);
      setFeedback({
        type: 'ok',
        msg: u.is_active
          ? t('adminUsers.feedback.disabled', { username: u.username })
          : t('adminUsers.feedback.enabled', { username: u.username }),
      });
      await load();
    } catch (e) {
      setFeedback({ type: 'err', msg: tError(formatApiDetail(e?.response?.data?.detail)) || t('adminUsers.error.generic') });
    }
  };

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="max-w-6xl mx-auto py-6 px-4">
        {/* C12.3 — Fusion visuelle Équipe & Magasins */}
        <AdminEquipeMagasinsTabs active="users" />
        <header className="flex items-center justify-between mb-4">
          <div>
            <button onClick={() => navigate('/dashboard')} className="text-gray-400 hover:text-white text-sm inline-flex items-center gap-1 mb-1">
              <ArrowLeft className="w-3.5 h-3.5" /> {t('adminUsers.action.back')}
            </button>
            <h1 className="text-2xl font-bold text-white inline-flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-emerald-400" /> {t('adminUsers.title')}
            </h1>
            <p className="text-sm text-gray-400 mt-1 inline-flex flex-wrap items-center gap-2">
              <span>{t('adminUsers.subtitle')}</span>
              <span
                className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10 text-gray-400"
                data-testid="user-limit-info"
              >
                {t('adminUsers.limitInfo', { current: limits.current, max: limits.max, plan: planLabel })}
              </span>
            </p>
          </div>
          <button
            type="button"
            onClick={openCreateModal}
            disabled={!limits.can_add}
            data-testid="admin-users-create-btn"
            title={!limits.can_add ? t('adminUsers.limitReachedTitle', { max: limits.max }) : ''}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded font-bold text-sm ${
              limits.can_add
                ? 'bg-emerald-600 hover:bg-emerald-500 text-white'
                : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            <UserPlus className="w-4 h-4" />
            {limits.can_add ? t('adminUsers.createUser') : t('adminUsers.limitReached', { max: limits.max })}
          </button>
        </header>

        {feedback && (
          <div className={`mb-3 p-3 rounded border text-sm ${feedback.type === 'ok' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300' : 'bg-red-500/10 border-red-500/30 text-red-300'}`}>
            {feedback.msg}
          </div>
        )}

        <label className="inline-flex items-center gap-2 text-sm text-gray-400 mb-3">
          <input type="checkbox" checked={showDisabled} onChange={(e) => setShowDisabled(e.target.checked)} />
          {t('adminUsers.includeDisabled')}
        </label>

        <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="admin-users-table">
            <thead className="bg-gray-800/60 text-gray-400 text-xs uppercase">
              <tr>
                <th className="text-left p-3">{t('adminUsers.field.username')}</th>
                <th className="text-left p-3">{t('adminUsers.col.role')}</th>
                <th className="text-left p-3">{t('adminUsers.field.assignedMagasins')}</th>
                <th className="text-left p-3">{t('adminUsers.col.emailTel')}</th>
                <th className="text-left p-3">{t('adminUsers.col.status')}</th>
                <th className="text-right p-3">{t('adminUsers.col.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} className="p-6 text-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin inline" /></td></tr>
              )}
              {!loading && users.length === 0 && (
                <tr><td colSpan={6} className="p-6 text-center text-gray-500">{t('adminUsers.empty')}</td></tr>
              )}
              {users.map((u) => (
                <tr key={u.id} className="border-t border-gray-800" data-testid={`admin-users-row-${u.username}`}>
                  <td className="p-3 font-bold text-white">{u.username}</td>
                  <td className="p-3">
                    <span className={`px-2 py-0.5 rounded border text-xs font-bold ${ROLE_BADGE[u.magasin_role] || 'bg-gray-500/15 text-gray-300 border-gray-500/30'}`}>
                      {u.magasin_role || t('adminUsers.roleUnknown')}
                    </span>
                  </td>
                  <td className="p-3">
                    {u.assigned_magasins?.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {u.assigned_magasins.map((c) => <MagasinBadge key={c} code={c} size="sm" />)}
                      </div>
                    ) : (
                      <span className="text-xs text-gray-500 italic">{t('adminUsers.allMagasins')}</span>
                    )}
                  </td>
                  <td className="p-3 text-gray-300 text-xs">
                    <div>{u.email || <span className="text-gray-600">—</span>}</div>
                    <div className="text-gray-500">{u.telephone}</div>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-col gap-1">
                      {u.is_active ? (
                        <span className="text-emerald-300 text-xs font-bold">{t('adminUsers.status.active')}</span>
                      ) : (
                        <span className="text-red-300 text-xs font-bold">{t('adminUsers.status.disabled')}</span>
                      )}
                      {u.pos_only && u.is_active && (
                        <span className="text-amber-300 text-[10px] font-bold uppercase tracking-wide" data-testid={`admin-users-pos-only-${u.username}`}>
                          {t('adminUsers.status.posOnly')}
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <button
                      type="button"
                      onClick={() => openEditModal(u)}
                      data-testid={`admin-users-edit-${u.username}`}
                      className="inline-flex items-center gap-1 text-sky-400 hover:text-sky-300 text-xs mr-2"
                    >
                      <Pencil className="w-3.5 h-3.5" /> {t('adminUsers.action.edit')}
                    </button>
                    {['vendeur', 'technicien', 'admin_magasin'].includes(u.magasin_role) && (
                      <button
                        type="button"
                        onClick={() => setCommissionsUser(u)}
                        data-testid={`admin-users-commissions-${u.username}`}
                        className="inline-flex items-center gap-1 text-amber-400 hover:text-amber-300 text-xs mr-2"
                        title={t('adminUsers.commissions')}
                      >
                        <Percent className="w-3.5 h-3.5" /> {t('adminUsers.commissions')}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => handleToggle(u)}
                      data-testid={`admin-users-toggle-${u.username}`}
                      className={`inline-flex items-center gap-1 text-xs ${u.is_active ? 'text-amber-400 hover:text-amber-300' : 'text-emerald-400 hover:text-emerald-300'}`}
                    >
                      {u.is_active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                      {u.is_active ? t('adminUsers.action.disable') : t('adminUsers.action.enable')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </div>
      <UserModal
        open={modal.open}
        initial={modal.initial}
        onClose={() => { setModal({ open: false, initial: null }); setModalFormError(null); }}
        onSubmit={handleSubmit}
        busy={busy}
        allMagasins={allMagasins}
        formError={modalFormError}
      />
      <CommissionsModal
        user={commissionsUser}
        api={api}
        onClose={() => setCommissionsUser(null)}
      />
    </Layout>
  );
}
